import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, CircleNotch, Plus, X, MagicWand } from "@phosphor-icons/react";

export default function KbSuggestions({ agentId, onAccepted }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get(`/agents/${agentId}/kb-suggestions`).then((r) => setSuggestions(r.data)).catch(() => {});
  }, [agentId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/kb-suggestions/generate`);
      setSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("Inga nya kunskapsluckor hittades 🎉");
      else toast.success(`${data.suggestions.length} förslag hittade`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte analysera luckor");
    } finally { setLoading(false); }
  };

  const accept = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/agents/${agentId}/kb-suggestions/${s.id}/accept`);
      setSuggestions((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Tillagd i kunskapsbasen");
      onAccepted?.();
    } catch { toast.error("Kunde inte lägga till"); }
    finally { setBusyId(null); }
  };

  const dismiss = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/agents/${agentId}/kb-suggestions/${s.id}/dismiss`);
      setSuggestions((arr) => arr.filter((x) => x.id !== s.id));
    } catch { toast.error("Kunde inte avfärda"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-transparent p-5" data-testid="kb-suggestions">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><MagicWand size={20} weight="fill" className="text-violet-500" /> Självförbättrande kunskap</h3>
          <p className="text-sm text-zinc-500 mt-0.5 max-w-xl">AI:n föreslår nya artiklar automatiskt — från lösta ärenden, positiv feedback (👍) och eskalerade frågor. Du granskar och godkänner innan något läggs till.</p>
        </div>
        <button data-testid="kb-suggest-button" onClick={generate} disabled={loading}
          className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-60">
          {loading ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
          {loading ? "Analyserar…" : "Hitta kunskapsluckor"}
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-4 space-y-3">
          {suggestions.map((s) => (
            <div key={s.id} data-testid="kb-suggestion-item" className="bg-white border border-zinc-200 rounded-lg p-4">
              {s.source && s.source !== "gap" && (
                <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded mb-1.5 ${s.source === "csat_positive" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                  {s.source === "csat_positive" ? "Från positivt betyg" : "Från löst ärende"}
                </span>
              )}
              <div className="font-semibold text-sm text-zinc-800">{s.question}</div>
              <p className="text-sm text-zinc-600 mt-1">{s.answer}</p>
              {s.reason && <p className="text-xs text-zinc-400 mt-1 italic">{s.reason}</p>}
              <div className="flex gap-2 mt-3">
                <button data-testid="kb-accept" onClick={() => accept(s)} disabled={busyId === s.id}
                  className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
                  {busyId === s.id ? <CircleNotch size={13} className="animate-spin" /> : <Plus size={13} weight="bold" />} Lägg till
                </button>
                <button data-testid="kb-dismiss" onClick={() => dismiss(s)} disabled={busyId === s.id}
                  className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-3 py-1.5 text-xs font-semibold text-zinc-500">
                  <X size={13} weight="bold" /> Avfärda
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
